import { useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useRouter } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { ErrorState } from '@/components/common/ErrorState';
import { PageLoader } from '@/components/common/PageLoader';
import { Button } from '@/components/ui/button';
import {
  pcmpProjectDetailQueryOptions,
  pcmpProjectOptionsQueryOptions,
} from '../api/pcmpProjects.api';
import {
  PcmpProjectForm,
  type PcmpProjectFormHandle,
} from '../components/PcmpProjectForm/PcmpProjectForm';
import { useUpdatePcmpProject } from '../hooks/useUpdatePcmpProject';
import type { PcmpProjectFormValues } from '../types/pcmpProjectForm.schema';
import { toPcmpProjectFormValues, toPcmpProjectPayload } from '../utils/pcmpProjectFormMappers';

export function PcmpProjectEditPage() {
  const { projectId } = useParams({ from: '/_auth/pcmp/projects/$projectId/edit' });
  const router = useRouter();
  const formRef = useRef<PcmpProjectFormHandle>(null);
  const submittedValuesRef = useRef<PcmpProjectFormValues | null>(null);

  const project = useQuery(pcmpProjectDetailQueryOptions(projectId));
  const options = useQuery(pcmpProjectOptionsQueryOptions());
  const defaultValues = useMemo(
    () => (project.data ? toPcmpProjectFormValues(project.data) : undefined),
    [project.data],
  );

  function goToDetail() {
    void router.navigate({ to: '/pcmp/projects/$projectId', params: { projectId } });
  }

  const { mutate: updateProject, isPending } = useUpdatePcmpProject(projectId, () => {
    if (submittedValuesRef.current) formRef.current?.reset(submittedValuesRef.current);
    goToDetail();
  });

  function handleSubmit(values: PcmpProjectFormValues) {
    submittedValuesRef.current = values;
    updateProject(toPcmpProjectPayload(values));
  }

  const error = project.error ?? options.error;
  const isLoading = project.isLoading || options.isLoading;

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-5">
      <div className="flex min-w-0 items-center gap-3">
        <Button type="button" variant="outline" size="sm" onClick={goToDetail} className="gap-1.5">
          <ArrowLeft className="size-3.5" />
          Back
        </Button>
        <h1 className="truncate text-2xl font-semibold">
          Edit {project.data?.displayName ?? 'Project'}
        </h1>
      </div>

      {isLoading ? (
        <PageLoader />
      ) : error || !project.data || !options.data || !defaultValues ? (
        <ErrorState
          error={error}
          fallbackTitle="Could not load this project"
          onRetry={() => {
            void project.refetch();
            void options.refetch();
          }}
          isRetrying={project.isFetching || options.isFetching}
          fill
        />
      ) : (
        <PcmpProjectForm
          ref={formRef}
          defaultValues={defaultValues}
          options={options.data}
          project={project.data}
          onSubmit={handleSubmit}
          onCancel={goToDetail}
          submitLabel="Save Changes"
          pendingLabel="Saving..."
          isPending={isPending}
        />
      )}
    </div>
  );
}
